import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext } from "react";
import { AnyFieldApi } from "@tanstack/react-form";
import { Skeleton } from "./Skeleton";
import { Label } from "./Label";
import { QuestionMarkTooltip } from "./QuestionMarkTooltip";
import { cn } from "@utils/cn";
import { FieldTestIdContext, FormFieldTestIdPrefixContext } from "./form-control-test-id";
import { fieldTestId } from "@/utils/test-id";
function formatFieldErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error && (error as {
    message?: unknown;
  }).message != null) {
    return String((error as {
      message: unknown;
    }).message);
  }
  return String(error);
}

//context for the form
const TanStackFormContext = createContext<any>(null);

// Provider
export function TanStackFormProvider(t0) {
  const $ = _c(6);
  const {
    form,
    fieldTestIdPrefix,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== fieldTestIdPrefix) {
    t1 = <FormFieldTestIdPrefixContext.Provider value={fieldTestIdPrefix}>{children}</FormFieldTestIdPrefixContext.Provider>;
    $[0] = children;
    $[1] = fieldTestIdPrefix;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== form || $[4] !== t1) {
    t2 = <TanStackFormContext.Provider value={form}>{t1}</TanStackFormContext.Provider>;
    $[3] = form;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// Hook
export function useTanStackForm() {
  const form = useContext(TanStackFormContext);
  if (!form) {
    throw new Error("useTanStackForm must be used within TanStackFormProvider");
  }
  return form;
}

// Types
export interface TanStackFormItemProps<TFormData> {
  name: string;
  label?: string;
  labelDescription?: string;
  questionMarkTooltip?: string;
  labelClassName?: string;
  children: (field: AnyFieldApi) => React.ReactNode;
  validators?: any;
  inputProps?: any;
  labelProps?: any;
  required?: boolean;
  isLoading?: boolean;
  touched?: boolean;
}

// Form Label
const FormLabel = React.memo(t0 => {
  const $ = _c(17);
  const {
    label,
    labelDescription,
    questionMarkTooltip,
    labelClassName,
    name,
    required,
    isLoading
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="w-32 h-8 rounded-md" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!label) {
    return null;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignSelf: "start"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== required) {
    t2 = required && <span className="text-red-600 ml-1">*</span>;
    $[2] = required;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== questionMarkTooltip) {
    t3 = questionMarkTooltip && <QuestionMarkTooltip tooltip={questionMarkTooltip} />;
    $[4] = questionMarkTooltip;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label || $[7] !== labelClassName || $[8] !== name || $[9] !== t2 || $[10] !== t3) {
    t4 = <Label htmlFor={name} className={labelClassName} style={t1}>{label}{t2}{t3}</Label>;
    $[6] = label;
    $[7] = labelClassName;
    $[8] = name;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== labelDescription) {
    t5 = labelDescription && <span className="text-xs text-gray-500">{labelDescription}</span>;
    $[12] = labelDescription;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});
FormLabel.displayName = "FormLabel";

// Form Item
export function TanStackFormItem(t0) {
  const $ = _c(34);
  const {
    name,
    label,
    labelDescription,
    questionMarkTooltip,
    labelClassName,
    children,
    validators,
    inputProps,
    labelProps,
    required,
    touched: t1,
    isLoading
  } = t0;
  const touched = t1 === undefined ? true : t1;
  const form = useTanStackForm();
  const fieldTestIdPrefix = useContext(FormFieldTestIdPrefixContext);
  const t2 = (labelProps?.className || inputProps?.className) && "gap-2";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("grid grid-cols-12", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = labelProps?.className;
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex flex-col col-span-12 lg:col-span-4", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isLoading || $[5] !== label || $[6] !== labelClassName || $[7] !== labelDescription || $[8] !== name || $[9] !== questionMarkTooltip || $[10] !== required) {
    t6 = <FormLabel label={label} labelDescription={labelDescription} questionMarkTooltip={questionMarkTooltip} labelClassName={labelClassName} name={name} required={required} isLoading={isLoading} />;
    $[4] = isLoading;
    $[5] = label;
    $[6] = labelClassName;
    $[7] = labelDescription;
    $[8] = name;
    $[9] = questionMarkTooltip;
    $[10] = required;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== labelProps || $[13] !== t5 || $[14] !== t6) {
    t7 = <div {...labelProps} className={t5}>{t6}</div>;
    $[12] = labelProps;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = inputProps?.className;
  let t9;
  if ($[16] !== t8) {
    t9 = cn("lg:col-span-8 col-span-12", t8);
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== children || $[19] !== fieldTestIdPrefix || $[20] !== form || $[21] !== isLoading || $[22] !== name || $[23] !== touched || $[24] !== validators) {
    t10 = isLoading ? <Skeleton className="h-10 w-full rounded-md" /> : <form.Field name={name} validators={validators}>{field => {
        let hasError;
        if (touched) {
          hasError = field.state.meta.isTouched && !field.state.meta.isValid;
        } else {
          hasError = !field.state.meta.isValid;
        }
        return <FieldTestIdContext.Provider value={fieldTestIdPrefix ? fieldTestId(fieldTestIdPrefix, name) : undefined}>{children(field)}{hasError && field.state.meta.errors[0] != null && <p className="mb-0 mt-1 text-red-600 text-sm">{formatFieldErrorMessage(field.state.meta.errors[0])}</p>}</FieldTestIdContext.Provider>;
      }}</form.Field>;
    $[18] = children;
    $[19] = fieldTestIdPrefix;
    $[20] = form;
    $[21] = isLoading;
    $[22] = name;
    $[23] = touched;
    $[24] = validators;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== inputProps || $[27] !== t10 || $[28] !== t9) {
    t11 = <div {...inputProps} className={t9}>{t10}</div>;
    $[26] = inputProps;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t3 || $[32] !== t7) {
    t12 = <div className={t3}>{t7}{t11}</div>;
    $[30] = t11;
    $[31] = t3;
    $[32] = t7;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}